import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useI18n } from "../i18n";

const STORAGE_KEY = "goldora_cookie_consent_v1";

export default function CookieBanner() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const t = setTimeout(() => setOpen(true), 1500);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  const save = (consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, at: new Date().toISOString() }));
    } catch {}
    setOpen(false);
  };
  const acceptAll = () => save({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => save({ essential: true, analytics: false, marketing: false });
  const saveCustom = () => save(prefs);

  if (!open) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[90] p-4 flex justify-center pointer-events-none" data-testid="cookie-banner">
      <div className="pointer-events-auto max-w-3xl w-full surface-elevated border border-gold-500/20 rounded-xl p-5 shadow-2xl backdrop-blur-md">
        <div className="flex items-start gap-3">
          <Cookie size={20} className="text-gold-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-display text-lg tracking-tight">
              {pt ? "Cookies e privacidade" : "Cookies and privacy"}
            </h3>
            <p className="mt-1.5 text-sm text-neutral-300 leading-relaxed">
              {pt
                ? "Usamos cookies essenciais para o funcionamento da plataforma. Cookies de análise são opcionais e nos ajudam a entender o uso. Não vendemos dados a terceiros — nunca. "
                : "We use essential cookies for the platform to work. Analytics cookies are optional and help us understand usage. We never sell data to third parties. "}
              <Link to="/privacidade" className="text-gold-400 underline underline-offset-2" data-testid="cookie-privacy-link">
                {pt ? "Ver política completa" : "Read full policy"}
              </Link>
            </p>

            {showCustom && (
              <div className="mt-4 space-y-2 text-sm">
                <Pref label={pt ? "Essenciais" : "Essential"} desc={pt ? "Login, sessão, segurança. Não podem ser desligados." : "Login, session, security. Can't be turned off."} checked disabled />
                <Pref
                  label={pt ? "Análise" : "Analytics"}
                  desc={pt ? "Métricas anônimas de uso para melhorar a plataforma." : "Anonymous usage metrics to improve the platform."}
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testid="pref-analytics"
                />
                <Pref
                  label={pt ? "Marketing" : "Marketing"}
                  desc={pt ? "Personalização de campanhas (não vendemos seus dados)." : "Campaign personalization (we don't sell your data)."}
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  testid="pref-marketing"
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {!showCustom ? (
                <>
                  <button onClick={acceptAll} data-testid="cookie-accept-all" className="bg-gold-500 hover:bg-gold-400 text-black text-sm font-medium px-4 py-2 rounded-md transition-colors">
                    {pt ? "Aceitar todos" : "Accept all"}
                  </button>
                  <button onClick={rejectAll} data-testid="cookie-reject-all" className="border border-white/15 hover:border-white/30 text-sm px-4 py-2 rounded-md transition-colors">
                    {pt ? "Recusar opcionais" : "Reject optional"}
                  </button>
                  <button onClick={() => setShowCustom(true)} data-testid="cookie-customize" className="text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-white px-3 py-2">
                    {pt ? "Personalizar" : "Customize"}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={saveCustom} data-testid="cookie-save-prefs" className="bg-gold-500 hover:bg-gold-400 text-black text-sm font-medium px-4 py-2 rounded-md transition-colors">
                    {pt ? "Salvar preferências" : "Save preferences"}
                  </button>
                  <button onClick={() => setShowCustom(false)} className="text-xs text-neutral-400 hover:text-white px-3 py-2">
                    {pt ? "Voltar" : "Back"}
                  </button>
                </>
              )}
            </div>
          </div>
          <button onClick={rejectAll} aria-label="Close" className="p-1 text-neutral-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

const Pref = ({ label, desc, checked, onChange, disabled, testid }) => (
  <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => !disabled && onChange?.(e.target.checked)}
      disabled={disabled}
      className="mt-1 accent-gold-500"
      data-testid={testid}
    />
    <div>
      <div className="text-white text-sm">{label}</div>
      <div className="text-neutral-500 text-xs">{desc}</div>
    </div>
  </label>
);
